import { useEffect, useState } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { useToast } from '@/hooks/useToast'
import { useCreateCategory, useUpdateCategory } from '@/features/inbox/queries'
import type { InboxCategory, InboxCategoryColor } from '@/features/inbox/types'

const COLORS: { value: InboxCategoryColor; label: string }[] = [
  { value: 'primary', label: 'Brand primary' },
  { value: 'brand', label: 'Brand accent' },
  { value: 'info', label: 'Blue' },
  { value: 'success', label: 'Green' },
  { value: 'warning', label: 'Amber' },
  { value: 'secondary', label: 'Neutral' },
]

const ICONS = ['Tag', 'Mail', 'FileText', 'Receipt', 'Landmark', 'ScrollText', 'User', 'Bell', 'Newspaper']

export function CategoryFormModal({
  open,
  onOpenChange,
  category,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  category: InboxCategory | null
}) {
  const { toast } = useToast()
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const [name, setName] = useState('')
  const [color, setColor] = useState<InboxCategoryColor>('secondary')
  const [icon, setIcon] = useState('Tag')

  useEffect(() => {
    if (open) {
      setName(category?.name ?? '')
      setColor(category?.color ?? 'secondary')
      setIcon(category?.icon ?? 'Tag')
    }
  }, [open, category])

  const busy = createCategory.isPending || updateCategory.isPending

  const onSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }
    try {
      if (category) {
        await updateCategory.mutateAsync({ id: category.id, body: { name, color, icon } })
        toast({ title: 'Category updated' })
      } else {
        await createCategory.mutateAsync({ name, color, icon })
        toast({ title: 'Category created' })
      }
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed'
      toast({ title: 'Could not save category', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={category ? 'Edit category' : 'New category'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Consultants"
        />
        <FormField
          label="Colour"
          control={
            <Combobox<InboxCategoryColor>
              value={color}
              onChange={(v) => setColor(v ?? 'secondary')}
              options={COLORS}
            />
          }
        />
        <FormField
          label="Icon"
          control={
            <Combobox
              value={icon}
              onChange={(v) => setIcon(v ?? 'Tag')}
              options={ICONS.map((i) => ({ value: i, label: i }))}
            />
          }
        />
      </div>
    </Modal>
  )
}
